import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Heart, Plus, CreditCard, CheckCircle, Upload, Camera } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import CandidateCard from '../../components/CandidateCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const EMPTY_CANDIDATE = {
  name: '', fatherName: '', motherName: '', nukh: '',
  gender: '', dateOfBirth: '', birthTime: '', birthPlace: '',
  height: '', weight: '', complexion: '', bloodGroup: '',
  education: '', occupation: '', workingAt: '', annualIncome: '',
  phone: '', email: '', whatsapp: '',
  currentAddress: '', currentCity: '', currentState: '', currentCountry: 'India',
  nativePlace: '', nativeCity: '', nativeState: '',
  hobbies: '', aboutCandidate: '', partnerPreference: '',
  mapiOrManglik: '', maritalStatus: '',
  unmarriedBrothers: '', marriedBrothers: '', unmarriedSisters: '', marriedSisters: '',
  maternalName: '', nukhOfMaternals: '', placeOfMaternals: '',
};

const EMPTY_ATTENDEE = { name: '', relation: '', phone: '' };

const ParichaySangamRegistration = () => {
  const nextClientId = useRef(1);
  const [candidates, setCandidates] = useState([{ ...EMPTY_CANDIDATE, _clientId: '0' }]);
  const [attendingInPerson, setAttendingInPerson] = useState(false);
  const [attendees, setAttendees] = useState([
    { ...EMPTY_ATTENDEE, relation: 'Parent / Guardian 1' },
    { ...EMPTY_ATTENDEE, relation: 'Parent / Guardian 2' },
    { ...EMPTY_ATTENDEE, relation: 'Family Member / Attendee' },
  ]);
  const [paymentData, setPaymentData] = useState({
    paymentMode: '', paymentDate: '', transactionNumber: '', paymentAmount: ''
  });
  const [nukhOptions, setNukhOptions] = useState([]);
  const [expandedCandidates, setExpandedCandidates] = useState([0]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [candidateFiles, setCandidateFiles] = useState([{ photo: null, idCard: null }]);
  const [attendeeFiles, setAttendeeFiles] = useState([null, null, null]);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const res = await axios.get(`${API}/family-registration/options`);
      setNukhOptions(res.data.nukhs || []);
    } catch (e) {
      console.error('Failed to fetch options');
    }
  };

  const totalAttendingMembers = attendingInPerson
    ? candidates.length + attendees.filter(a => a.name.trim()).length
    : 0;
  const totalFee = totalAttendingMembers * 500;

  const toggleCandidate = (idx) => {
    setExpandedCandidates(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const addCandidate = () => {
    if (candidates.length >= 3) { toast.error('Maximum 3 candidates allowed'); return; }
    const newId = String(nextClientId.current++);
    setCandidates(prev => [...prev, { ...EMPTY_CANDIDATE, _clientId: newId }]);
    setCandidateFiles(prev => [...prev, { photo: null, idCard: null }]);
    setExpandedCandidates(prev => [...prev, candidates.length]);
  };

  const removeCandidate = (idx) => {
    if (candidates.length <= 1) return;
    setCandidates(prev => prev.filter((_, i) => i !== idx));
    setCandidateFiles(prev => prev.filter((_, i) => i !== idx));
    setExpandedCandidates(prev => prev.filter(i => i !== idx).map(i => i > idx ? i - 1 : i));
  };

  const handleFileChange = (idx, fileType, file) => {
    setCandidateFiles(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [fileType]: file };
      return updated;
    });
  };

  const handleAttendeeFileChange = (idx, file) => {
    setAttendeeFiles(prev => {
      const updated = [...prev];
      updated[idx] = file;
      return updated;
    });
  };

  const handleCandidateDataChange = (idx, newData) => {
    setCandidates(prev => {
      const updated = [...prev];
      updated[idx] = { ...newData, _clientId: prev[idx]._clientId };
      return updated;
    });
  };

  const handleAttendeeChange = (idx, field, value) => {
    setAttendees(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      const required = ['name', 'fatherName', 'nukh', 'gender', 'maritalStatus', 'dateOfBirth', 'education', 'phone', 'currentCity', 'currentState'];
      for (const field of required) {
        if (!c[field]?.trim()) {
          toast.error(`Please fill ${field} for Candidate ${i + 1}`);
          setExpandedCandidates(prev => prev.includes(i) ? prev : [...prev, i]);
          return false;
        }
      }
    }
    if (attendingInPerson && totalFee > 0) {
      if (!paymentData.paymentMode) { toast.error('Please select payment mode'); return false; }
      if (!paymentData.paymentDate) { toast.error('Please enter payment date'); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      const cleanCandidates = candidates.map(({ _clientId, ...rest }) => rest);
      const jsonBody = {
        candidates: cleanCandidates,
        attendingInPerson,
        attendees: attendingInPerson ? attendees : [],
        ...paymentData,
      };
      formData.append('formData', JSON.stringify(jsonBody));
      candidateFiles.forEach((files, idx) => {
        if (files.photo) formData.append(`candidate_${idx}_photo`, files.photo);
        if (files.idCard) formData.append(`candidate_${idx}_idCard`, files.idCard);
      });
      attendeeFiles.forEach((file, idx) => {
        if (file) formData.append(`attendee_${idx}_photo`, file);
      });
      const res = await axios.post(`${API}/parichay-sangam/register`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
      setSubmittedData(res.data.registration);
      toast.success('Registration submitted successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && submittedData) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Submitted!</h2>
            <p className="text-gray-600 mb-4">Your Parichay Sangam 2026 registration has been submitted successfully.</p>
            <div className="bg-green-50 p-4 rounded-lg text-left space-y-2 mb-6">
              <p><strong>Family Code:</strong> {submittedData.familyCode}</p>
              {submittedData.candidates?.map((c, i) => (
                <p key={i}><strong>Candidate {i + 1} ID:</strong> {c.candidateId} — {c.name}</p>
              ))}
              <p><strong>Total Attending:</strong> {submittedData.totalAttendingMembers}</p>
              {submittedData.totalFee > 0 && <p><strong>Total Fee:</strong> ₹{submittedData.totalFee}</p>}
            </div>
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded">Your registration is pending admin approval. You will be notified once it is approved.</p>
            <Button className="mt-6" onClick={() => window.location.href = '/matrimonial'}>Back to Matrimonial</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="gradient-orange py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div>
            <Heart className="w-12 h-12 text-white mx-auto mb-4" />
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Parichay Sangam 2026 Registration Form
            </h1>
            <p className="text-white/80 text-lg">परिचय संगम 2026 पंजीकरण प्रपत्र</p>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-8 text-sm">
          <p className="font-semibold text-amber-800">Important / महत्वपूर्ण:</p>
          <ul className="list-disc ml-5 text-amber-700 mt-1 space-y-1">
            <li>You can register up to 3 candidates in one form / एक फॉर्म में अधिकतम 3 उम्मीदवार दर्ज कर सकते हैं</li>
            <li>Fee: ₹500 per attending member / शुल्क: ₹500 प्रति उपस्थित सदस्य</li>
            <li>Fields marked with <span className="text-destructive">*</span> are mandatory / <span className="text-destructive">*</span> चिह्नित फ़ील्ड अनिवार्य हैं</li>
          </ul>
        </div>

        {/* Candidates */}
        {candidates.map((candidate, idx) => (
          <CandidateCard
            key={candidate._clientId}
            initialData={candidate}
            idx={idx}
            expanded={expandedCandidates.includes(idx)}
            onToggle={toggleCandidate}
            onDataChange={handleCandidateDataChange}
            onFileChange={handleFileChange}
            onDelete={removeCandidate}
            canDelete={candidates.length > 1}
            nukhOptions={nukhOptions}
            candidateFiles={candidateFiles[idx]}
          />
        ))}

        {candidates.length < 3 && (
          <Button variant="outline" onClick={addCandidate} className="mb-8 w-full border-dashed border-2">
            <Plus className="w-4 h-4 mr-2" /> Add Another Candidate / एक और उम्मीदवार जोड़ें ({candidates.length}/3)
          </Button>
        )}

        {/* Attending In-Person */}
        <Card className="p-6 mb-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={attendingInPerson}
              onChange={(e) => setAttendingInPerson(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-primary"
            />
            <div>
              <p className="font-semibold text-gray-900">Would you be attending Parichay Sangam 2026 in-person?</p>
              <p className="text-sm text-gray-500">क्या आप परिचय संगम 2026 में व्यक्तिगत रूप से उपस्थित होंगे?</p>
            </div>
          </label>

          {attendingInPerson && (
            <div className="mt-6 space-y-4">
              <h4 className="font-semibold text-gray-700 border-b pb-2">Other Attendees / अन्य उपस्थित सदस्य</h4>
              {attendees.map((attendee, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm">{attendee.relation} - Name / नाम</Label>
                      <Input value={attendee.name} onChange={(e) => handleAttendeeChange(idx, 'name', e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-sm">Relation / संबंध</Label>
                      <Input value={attendee.relation} onChange={(e) => handleAttendeeChange(idx, 'relation', e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-sm">Phone / फ़ोन</Label>
                      <Input value={attendee.phone} onChange={(e) => handleAttendeeChange(idx, 'phone', e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  {attendee.name.trim() && (
                    <div>
                      <Label className="text-sm flex items-center gap-1">
                        <Camera className="w-3.5 h-3.5" /> Photo for ID Card <span className="text-gray-400 text-xs">/ पहचान पत्र के लिए फोटो</span>
                      </Label>
                      <div className="mt-1">
                        <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors" data-testid={`attendee-${idx}-photo-upload`}>
                          <Upload className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-500 truncate">
                            {attendeeFiles[idx] ? attendeeFiles[idx].name : 'Choose photo (JPG, PNG, max 5MB)'}
                          </span>
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleAttendeeFileChange(idx, e.target.files[0])} />
                        </label>
                        {attendeeFiles[idx] && (
                          <div className="mt-2 flex items-center gap-2">
                            <img src={URL.createObjectURL(attendeeFiles[idx])} alt="Preview" className="w-16 h-16 object-cover rounded border" />
                            <button onClick={() => handleAttendeeFileChange(idx, null)} className="text-xs text-red-500 hover:underline">Remove</button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-blue-600">Total Candidates / कुल उम्मीदवार</p>
                    <p className="font-bold text-lg text-blue-800">{candidates.length}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Total Attending / कुल उपस्थित</p>
                    <p className="font-bold text-lg text-blue-800">{totalAttendingMembers}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Total Fee / कुल शुल्क</p>
                    <p className="font-bold text-lg text-blue-800">₹{totalFee}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Payment Section */}
        {attendingInPerson && totalFee > 0 && (
          <Card className="p-6 mb-6">
            <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Payment Details / भुगतान विवरण
            </h3>

            <div className="bg-amber-50 p-3 rounded-lg mb-4 text-sm text-amber-800">
              <p className="font-semibold">Amount Due / देय राशि: ₹{totalFee} ({totalAttendingMembers} members x ₹500)</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-sm">Payment Mode / भुगतान माध्यम <span className="text-destructive">*</span></Label>
                <div className="space-y-2 mt-2">
                  {[
                    { value: 'upi', label: 'UPI / QR Code' },
                    { value: 'bank', label: 'Bank Transfer / बैंक ट्रांसफर' },
                    { value: 'cash', label: 'Cash / नगद' },
                    { value: 'other', label: 'Other / अन्य' },
                  ].map(opt => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="paymentMode" value={opt.value} checked={paymentData.paymentMode === opt.value} onChange={handlePaymentChange} className="w-4 h-4" />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm">Amount Paid / भुगतान राशि (₹)</Label>
                <Input name="paymentAmount" type="number" value={paymentData.paymentAmount} onChange={handlePaymentChange} className="mt-1" placeholder={String(totalFee)} />
              </div>
            </div>

            {/* UPI QR Code */}
            {paymentData.paymentMode === 'upi' && (
              <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg mb-4">
                <h4 className="font-semibold text-blue-800 mb-3 text-center">UPI से भुगतान करें / Pay via UPI</h4>
                <div className="flex flex-col items-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_337dba5c-8d5d-4e7d-adac-9993bcb0c3e3/artifacts/uxuej4sd_WhatsApp%20Image%202026-02-15%20at%2013.14.34.jpeg"
                    alt="UPI QR Code"
                    className="max-w-xs w-full h-auto rounded-lg shadow-md mb-3"
                  />
                  <p className="text-sm text-blue-700 font-medium">UPI ID: boim-661059350985@boi</p>
                  <p className="text-xs text-blue-600 mt-1">किसी भी UPI ऐप से स्कैन करें / Scan with any UPI App</p>
                </div>
              </div>
            )}

            {/* Bank Transfer Details */}
            {paymentData.paymentMode === 'bank' && (
              <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg mb-4">
                <h4 className="font-semibold text-green-800 mb-3">बैंक विवरण / Bank Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium text-green-700">खाता नाम / Account Name:</span>
                      <p className="text-gray-800">BRAHMAKSHATRIYA MARVADI KHATRI ASSOCIATION</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-700">बैंक का नाम / Bank Name:</span>
                      <p className="text-gray-800">BANK OF INDIA</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-700">खाता संख्या / Account No:</span>
                      <p className="text-gray-800 font-mono">661020110000985</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-700">IFSC कोड / IFSC Code:</span>
                      <p className="text-gray-800 font-mono">BKID0006610</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-700">शाखा / Branch:</span>
                      <p className="text-gray-800">JODHPUR, RAJASTHAN</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-700">MICR कोड / MICR Code:</span>
                      <p className="text-gray-800 font-mono">342013002</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Payment Date / भुगतान तिथि <span className="text-destructive">*</span></Label>
                <Input name="paymentDate" type="date" value={paymentData.paymentDate} onChange={handlePaymentChange} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Transaction ID / लेन-देन संख्या</Label>
                <Input name="transactionNumber" value={paymentData.transactionNumber} onChange={handlePaymentChange} className="mt-1" placeholder="Enter transaction ID" />
              </div>
            </div>
          </Card>
        )}

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-6 text-lg"
          data-testid="parichay-submit-btn"
        >
          {submitting ? 'Submitting... / जमा हो रहा है...' : 'Submit Registration / पंजीकरण जमा करें'}
        </Button>
      </div>
    </div>
  );
};

export default ParichaySangamRegistration;
